import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Sprout, Users, Award, TrendingUp } from 'lucide-react';
import { services, projects, testimonials, companyInfo } from '../mockData';
import { ClientCarousel } from '../components/ClientCarousel';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1E5631] to-[#2d7a47] pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1595387426256-cc153122a6f1?auto=format&fit=crop&w=1600&q=80"
            alt="Beautiful landscaping"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-4xl animate-fade-in-up text-white text-center">
            {/* NEW HEADLINES */}
            <h1 className="font-heading text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Restoring Ecosystems.<br />Transforming Spaces.
            </h1>
            <p className="text-xl md:text-2xl text-green-100 mb-8 leading-relaxed max-w-3xl mx-auto">
              From Miyawaki Forests in Mining Zones to Luxury Landscapes in NCR.
            </p>
            
            {/* NEW DUAL BUTTONS FOR B2B & B2C */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/services?cat=corporate" 
                className="inline-flex items-center justify-center bg-[#4CAF50] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#45a049] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Corporate & CSR
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link 
                to="/services?cat=residential" 
                className="inline-flex items-center justify-center bg-white text-[#1E5631] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300 shadow-lg border-2 border-white hover:border-gray-200"
              >
                Residential Design
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Text Content */}
            <div className="order-1 lg:order-2">
              <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
                Who We Are
              </span>
              {/* UPDATED STRATEGIC TEXT */}
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Engineered Green Solutions
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Udyaan Landscaping specializes in high-impact ecological restoration. We are the partners of choice for Mining Afforestation in Odisha and Industrial Landscaping in Delhi NCR. We combine the science of the Miyawaki Method with the art of Landscape Design.
              </p>

              {/* NEW TRUST SIGNALS GRID */}
              <div className="grid grid-cols-3 gap-4 mb-8 pt-6 border-t border-gray-100">
                <div className="text-center">
                  <div className="font-bold text-[#1E5631] text-xl mb-1">Startup India</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Recognized</div>
                </div>
                <div className="text-center border-l border-gray-200">
                  <div className="font-bold text-[#1E5631] text-xl mb-1">Pan-India</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Service</div>
                </div>
                <div className="text-center border-l border-gray-200">
                  <div className="font-bold text-[#1E5631] text-xl mb-1">Scientific</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Soil Analysis</div>
                </div>
              </div>

              {/* Icon Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center space-x-3">
                  <Sprout className="w-8 h-8 text-[#4CAF50]" />
                  <span className="font-semibold text-gray-900">Sustainable</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="w-8 h-8 text-[#4CAF50]" />
                  <span className="font-semibold text-gray-900">Expert Team</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Award className="w-8 h-8 text-[#4CAF50]" />
                  <span className="font-semibold text-gray-900">Quality Work</span>
                </div>
                <div className="flex items-center space-x-3">
                  <TrendingUp className="w-8 h-8 text-[#4CAF50]" />
                  <span className="font-semibold text-gray-900">Growth Focus</span>
                </div>
              </div>
              
              <Link to="/about" className="inline-flex items-center text-[#1E5631] font-semibold text-lg hover:text-[#4CAF50] transition-colors mt-4">
                Learn More About Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>

            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <img
                src="https://images.pexels.com/photos/1453499/pexels-photo-1453499.jpeg"
                alt="Landscaping professional"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#4CAF50] text-white p-8 rounded-xl shadow-xl">
                <div className="text-5xl font-bold">100+</div>
                <div className="text-lg">Projects Completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Our Services
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Landscaping Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tailored for both Mining/Industrial Corporations and Residential Estates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={`${service.image}?auto=format&fit=crop&w=600&q=70`} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl font-semibold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {service.description.substring(0, 120)}...
                  </p>
                  <ul className="space-y-2 mb-6">
                    {service.benefits.slice(0, 3).map((benefit, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="w-4 h-4 text-[#4CAF50] mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary inline-flex items-center">
              View All Services
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Projects Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Our Work
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Featured Ecological & Design Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From restoring barren mining lands to creating lush home gardens.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project) => (
              <div
                key={project.id}
                className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-96 overflow-hidden">
                  <img
                    src={`${project.image}?auto=format&fit=crop&w=600&q=70`} 
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end">
                  <div className="p-6 text-white w-full">
                    <span className="inline-block bg-[#4CAF50] text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
                      {project.category}
                    </span>
                    <h3 className="font-heading text-2xl font-semibold mb-2">{project.title}</h3>
                    <p className="text-gray-200">{project.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/projects" className="btn-primary inline-flex items-center">
              View All Projects
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Client Logos Carousel */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ClientCarousel />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#4CAF50] font-semibold text-sm uppercase tracking-wide mb-4">
              Testimonials
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Read testimonials from satisfied clients across various projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gray-50 rounded-xl p-8 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-2xl text-[#4CAF50]">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-heading font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1E5631] to-[#2d7a47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=800&q=80"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Bring Nature Closer to You
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Get a free site evaluation and consultation for your landscaping project today
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-[#4CAF50] text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#45a049] transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
          >
            Get a Free Site Evaluation
            <ArrowRight className="ml-2 w-6 h-6" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;